import type {
  TravellerCount,
  TravellerTypeOption,
} from '@itineraries/entities/itinerary';
import type { Chapter } from '@travel-planner/shared/schemas';
import { TravellerCountList } from '@itineraries/entities/itinerary';
import { ArrowDown, ArrowUp, Layers, Plus, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { joinCss } from '@/shared/lib/css-utils';
import {
  Button,
  EmptyState,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  Select,
  Text,
} from '@/shared/ui';
import { useChapterRegions } from '../api/use-chapter-form-data';
import {
  CHAPTER_COLORS,
  dateFromDayNumber,
  dayCountBetween,
  dayNumberFromDate,
  nextChapterColor,
  renumberOrders,
} from '../model/chapter';
import {
  buildChapterTravellersPayload,
  isCustomChapterTravellers,
  readChapterTravellers,
} from '../model/chapter-travellers';
import css from './chapter-editor-modal.module.scss';

type CountryOption = { country: string; count: number };

type ChapterEditorModalProps = {
  isOpen: boolean;
  isPending: boolean;
  // Read-only historical-version view: chapters stay readable but Save and
  // Add chapter are hidden, so the block isn't a silent no-op.
  isReadOnly?: boolean;
  chapters: Chapter[];
  itineraryStartDate: string | null;
  itineraryEndDate: string | null;
  // Undated itineraries anchor chapter dates to a year-2000 sentinel;
  // the editor then shows "Day N" selects instead of calendar dates.
  undated: boolean;
  countries: CountryOption[];
  travellerTypeOptions: TravellerTypeOption[];
  typesById: Map<string, string>;
  // Itinerary-level travellers (aggregated). Shown as the "Default"
  // summary so the consultant can compare against the per-chapter
  // override they're about to set.
  defaultTravellers: TravellerCount[];
  onClose: () => void;
  onSave: (chapters: Chapter[]) => void;
};

type DraftRow = Chapter & {
  // FE-only — drives the radio toggle. Persisted as `null` (default)
  // or a `{ people: [...] }` jsonb on `travellers`; we re-hydrate by
  // sniffing `isCustomChapterTravellers(travellers)` on open.
  travellerMode: 'default' | 'custom';
  travellerCounts: TravellerCount[];
};

const makeDraftId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `draft-${Math.random().toString(36).slice(2)}`;

const dateOnly = (iso: string | null): string => (iso ?? '').slice(0, 10);

const lastChapterEnd = (chapters: Chapter[]): string | null => {
  if (chapters.length === 0) return null;
  return chapters.reduce<string | null>((acc, c) => {
    if (!acc) return c.endDate;
    return c.endDate > acc ? c.endDate : acc;
  }, null);
};

const addDayUtc = (date: string): string => {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

const hydrate = (
  chapters: Chapter[],
  typesById: Map<string, string>
): DraftRow[] =>
  chapters.map((c) => {
    const isCustom = isCustomChapterTravellers(c.travellers);
    return {
      ...c,
      travellerMode: isCustom ? 'custom' : 'default',
      travellerCounts: isCustom
        ? readChapterTravellers(c.travellers, typesById)
        : [],
    };
  });

const formatTravellerSummary = (counts: TravellerCount[]): string => {
  const positive = counts.filter((t) => t.count > 0);
  if (positive.length === 0) return 'None';
  return positive.map((t) => `${t.count} ${t.title}`).join(', ');
};

export const ChapterEditorModal = ({
  isOpen,
  isPending,
  isReadOnly = false,
  chapters,
  itineraryStartDate,
  itineraryEndDate,
  undated,
  countries,
  travellerTypeOptions,
  typesById,
  defaultTravellers,
  onClose,
  onSave,
}: ChapterEditorModalProps) => {
  const [draft, setDraft] = useState<DraftRow[]>(() =>
    hydrate(chapters, typesById)
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Re-hydrate from server data only on the *open transition* and when
  // the traveller-types map first lands — NOT on every new `chapters`
  // reference. A background refetch / query invalidation while the modal
  // is open hands `chapters` a fresh array identity even when the data is
  // unchanged; re-hydrating on that would silently wipe the consultant's
  // in-progress edits (perceived as "editing exited unexpectedly").
  const wasOpen = useRef(false);
  const typesHydrated = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      wasOpen.current = false;
      typesHydrated.current = false;
      return;
    }
    // `typesById` is empty until the traveller-types query resolves; the
    // first non-empty map is what turns raw ids into titles, so re-hydrate
    // once when it arrives (in addition to the initial open).
    const typesReady = typesById.size > 0;
    const justOpened = !wasOpen.current;
    const typesJustLanded = typesReady && !typesHydrated.current;
    if (!justOpened && !typesJustLanded) return;

    wasOpen.current = true;
    if (typesReady) typesHydrated.current = true;
    setDraft(hydrate(chapters, typesById));
    setErrorMessage(null);
  }, [isOpen, chapters, typesById]);

  const minDate = useMemo(
    () => dateOnly(itineraryStartDate),
    [itineraryStartDate]
  );
  const maxDate = useMemo(() => dateOnly(itineraryEndDate), [itineraryEndDate]);

  // "Day 1 … Day N" options for undated itineraries, sized to the
  // itinerary's duration (start → end anchor span).
  const dayOptions = useMemo(() => {
    if (!undated) return [];
    const count = dayCountBetween(minDate, maxDate);
    return Array.from({ length: count }, (_, i) => ({
      value: String(i + 1),
      label: `Day ${i + 1}`,
    }));
  }, [undated, minDate, maxDate]);

  const countryOptions = useMemo(
    () =>
      countries.map((c) => ({
        label: c.country,
        value: c.country,
      })),
    [countries]
  );

  const defaultSummary = useMemo(
    () => formatTravellerSummary(defaultTravellers),
    [defaultTravellers]
  );

  const handleAdd = () => {
    const fallbackStart = minDate || new Date().toISOString().slice(0, 10);
    const seedStart = lastChapterEnd(draft);
    const start = seedStart ? addDayUtc(seedStart) : fallbackStart;
    const end = maxDate && maxDate > start ? maxDate : start;
    setDraft((prev) => [
      ...prev,
      {
        id: makeDraftId(),
        title: '',
        country: null,
        regionId: null,
        travellers: null,
        startDate: start,
        endDate: end,
        color: nextChapterColor(prev.length),
        order: prev.length,
        travellerMode: 'default',
        travellerCounts: [],
      },
    ]);
  };

  const patchRow = (id: string, patch: Partial<DraftRow>) => {
    setDraft((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  };

  const removeRow = (id: string) => {
    setDraft((prev) => prev.filter((c) => c.id !== id));
  };

  const moveRow = (index: number, dir: -1 | 1) => {
    setDraft((prev) => {
      const target = index + dir;
      if (target < 0 || target >= prev.length) return prev;
      const a = prev[index];
      const b = prev[target];
      if (!a || !b) return prev;
      const next = prev.slice();
      next[index] = b;
      next[target] = a;
      return next;
    });
  };

  const handleSave = () => {
    setErrorMessage(null);
    for (const c of draft) {
      if (!c.title.trim()) {
        setErrorMessage('Every chapter needs a title.');
        return;
      }
      if (!c.startDate || !c.endDate) {
        setErrorMessage('Every chapter needs a start and end date.');
        return;
      }
      if (c.startDate > c.endDate) {
        setErrorMessage(
          `"${c.title}" — start date must be on or before end date.`
        );
        return;
      }
    }
    // Strip FE-only fields before mutation. The BE schema only knows
    // about the canonical `Chapter` shape; `travellerMode` +
    // `travellerCounts` collapse back into the `travellers` payload.
    const payload: Chapter[] = renumberOrders(draft).map((row) => {
      const { travellerMode, travellerCounts, ...rest } = row;
      return {
        ...rest,
        travellers: buildChapterTravellersPayload(
          travellerMode,
          travellerCounts
        ),
      };
    });
    onSave(payload);
  };

  return (
    <Modal className={css.modal} onClose={onClose} open={isOpen}>
      <ModalHeader onClose={onClose} title="Manage Chapters" />
      <div className={css.body}>
        <Text size="sm" variant="muted">
          Split the itinerary into chapters. Each chapter spans a date range,
          can override travellers for items inside its window, and tints the day
          rows in the planner with its colour.
        </Text>

        {draft.length === 0 ? (
          <EmptyState
            className={css.empty}
            description="Add chapters to organize your itinerary into parts."
            icon={<Layers size={28} />}
            title="No chapters yet."
          />
        ) : (
          <div className={css.list}>
            {draft.map((row, i) => (
              <ChapterRow
                country={row.country}
                countryOptions={countryOptions}
                defaultSummary={defaultSummary}
                defaultTravellers={defaultTravellers}
                isFirst={i === 0}
                isLast={i === draft.length - 1}
                dayOptions={dayOptions}
                isPending={isPending}
                key={row.id}
                maxDate={maxDate}
                minDate={minDate}
                onChange={(patch) => patchRow(row.id, patch)}
                onMoveDown={() => moveRow(i, 1)}
                onMoveUp={() => moveRow(i, -1)}
                onRemove={() => removeRow(row.id)}
                row={row}
                startDay={minDate}
                travellerTypeOptions={travellerTypeOptions}
                undated={undated}
              />
            ))}
          </div>
        )}

        <button
          aria-label="Add chapter"
          className={css.addRow}
          disabled={isReadOnly || isPending}
          onClick={handleAdd}
          title={
            isReadOnly
              ? 'Viewing a previous version — editing is disabled'
              : undefined
          }
          type="button"
        >
          <Plus size={14} />
          Add chapter
        </button>

        {errorMessage && <span className={css.error}>{errorMessage}</span>}
      </div>
      <ModalFooter>
        <Button
          disabled={isPending}
          onClick={onClose}
          type="button"
          variant="secondary"
        >
          {isReadOnly ? 'Close' : 'Cancel'}
        </Button>
        {/* Nothing to persist means no Save: an itinerary that has never had
            chapters shows only Add chapter (XT-489). `chapters.length` is part
            of the test on purpose — once chapters exist, removing them all
            must stay savable, otherwise Cancel would be the only way out and
            the removal could never be written back. */}
        {(draft.length > 0 || chapters.length > 0) && (
          <Button
            disabled={isReadOnly || isPending}
            onClick={handleSave}
            type="button"
            variant="primary"
          >
            {isPending ? 'Saving…' : 'Save chapters'}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

type ChapterRowProps = {
  row: DraftRow;
  countryOptions: { label: string; value: string }[];
  travellerTypeOptions: TravellerTypeOption[];
  defaultSummary: string;
  // Itinerary-level travellers (aggregated). Seeded into the counter
  // list the first time the consultant flips a chapter to "Custom"
  // so they start from the existing assignment rather than an empty
  // form — matches the user expectation that "Custom" forks from
  // the current state, not from zero.
  defaultTravellers: TravellerCount[];
  isPending: boolean;
  isFirst: boolean;
  isLast: boolean;
  minDate: string;
  maxDate: string;
  // Undated itineraries swap the calendar date inputs for "Day N"
  // selects. `startDay` is the Day-1 anchor; `dayOptions` are Day 1..N.
  undated: boolean;
  startDay: string;
  dayOptions: { label: string; value: string }[];
  country: string | null;
  onChange: (patch: Partial<DraftRow>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
};

// Sub-component per chapter so the per-row regions query (cascaded
// from country) is encapsulated and doesn't fan out across the whole
// modal. Regions for an unset country leave the dropdown disabled.
const ChapterRow = ({
  row,
  countryOptions,
  travellerTypeOptions,
  defaultSummary,
  defaultTravellers,
  isPending,
  isFirst,
  isLast,
  minDate,
  maxDate,
  undated,
  startDay,
  dayOptions,
  country,
  onChange,
  onMoveUp,
  onMoveDown,
  onRemove,
}: ChapterRowProps) => {
  // `options` are `{ label: title, value: id }` for the country's custom
  // regions — value is the region UUID stored on `row.regionId`.
  const { options: regionOptions } = useChapterRegions({
    country,
    enabled: !!country,
  });

  return (
    <div className={css.chapter}>
      <div className={css.chapterHeader}>
        <span
          aria-hidden="true"
          className={css.swatch}
          style={row.color ? { background: row.color } : undefined}
        />
        <Input
          aria-label="Chapter title"
          className={css.titleInput}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Chapter title"
          value={row.title}
        />
        <button
          aria-label="Move chapter up"
          className={css.iconBtn}
          disabled={isFirst || isPending}
          onClick={onMoveUp}
          type="button"
        >
          <ArrowUp size={14} />
        </button>
        <button
          aria-label="Move chapter down"
          className={css.iconBtn}
          disabled={isLast || isPending}
          onClick={onMoveDown}
          type="button"
        >
          <ArrowDown size={14} />
        </button>
        <button
          aria-label="Remove chapter"
          className={joinCss(css.iconBtn, css.danger)}
          disabled={isPending}
          onClick={onRemove}
          type="button"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className={css.fields}>
        {undated ? (
          <>
            <div className={css.field}>
              <label className={css.fieldLabel} htmlFor={`start-${row.id}`}>
                Start day
              </label>
              <Select
                aria-label="Start day"
                id={`start-${row.id}`}
                onChange={(e) =>
                  onChange({
                    startDate: dateFromDayNumber(
                      Number(e.target.value),
                      startDay
                    ),
                  })
                }
                options={dayOptions}
                usePortal
                value={String(dayNumberFromDate(row.startDate, startDay))}
              />
            </div>
            <div className={css.field}>
              <label className={css.fieldLabel} htmlFor={`end-${row.id}`}>
                End day
              </label>
              <Select
                aria-label="End day"
                id={`end-${row.id}`}
                onChange={(e) =>
                  onChange({
                    endDate: dateFromDayNumber(
                      Number(e.target.value),
                      startDay
                    ),
                  })
                }
                options={dayOptions}
                usePortal
                value={String(dayNumberFromDate(row.endDate, startDay))}
              />
            </div>
          </>
        ) : (
          <>
            <div className={css.field}>
              <label className={css.fieldLabel} htmlFor={`start-${row.id}`}>
                Start date
              </label>
              <input
                className={css.dateInput}
                id={`start-${row.id}`}
                max={maxDate || undefined}
                min={minDate || undefined}
                onChange={(e) => onChange({ startDate: e.target.value })}
                type="date"
                value={row.startDate}
              />
            </div>
            <div className={css.field}>
              <label className={css.fieldLabel} htmlFor={`end-${row.id}`}>
                End date
              </label>
              <input
                className={css.dateInput}
                id={`end-${row.id}`}
                max={maxDate || undefined}
                min={minDate || undefined}
                onChange={(e) => onChange({ endDate: e.target.value })}
                type="date"
                value={row.endDate}
              />
            </div>
          </>
        )}
        <div className={css.field}>
          <label className={css.fieldLabel} htmlFor={`country-${row.id}`}>
            Country
          </label>
          <Select
            aria-label="Country"
            id={`country-${row.id}`}
            onChange={(e) =>
              onChange({
                country: e.target.value || null,
                // Region cascades off country — drop it whenever the
                // user picks a different one so we don't ship a region
                // that no longer matches.
                regionId: null,
              })
            }
            options={countryOptions}
            placeholder="Select country"
            searchable
            // Portal the menu to document.body (z-index: 2100) so it floats
            // above the Modal overlay instead of being clipped inside the
            // scrollable modal body.
            usePortal
            value={row.country ?? ''}
          />
        </div>
        <div className={css.field}>
          <label className={css.fieldLabel} htmlFor={`region-${row.id}`}>
            Region
          </label>
          <Select
            aria-label="Region"
            disabled={!row.country || regionOptions.length === 0}
            id={`region-${row.id}`}
            onChange={(e) => onChange({ regionId: e.target.value || null })}
            options={regionOptions}
            placeholder={
              row.country
                ? regionOptions.length === 0
                  ? 'No regions available'
                  : 'Select region'
                : 'Pick a country first'
            }
            usePortal
            value={row.regionId ?? ''}
          />
        </div>
      </div>

      <div className={css.field}>
        <span className={css.fieldLabel}>Colour</span>
        <div className={css.colors}>
          {CHAPTER_COLORS.map((swatch) => (
            <button
              aria-label={`Pick colour ${swatch}`}
              className={joinCss(
                css.colorSwatch,
                swatch === row.color && css.colorSwatchActive
              )}
              key={swatch}
              onClick={() => onChange({ color: swatch })}
              style={
                {
                  '--swatch-color': swatch,
                } as React.CSSProperties
              }
              type="button"
            />
          ))}
        </div>
      </div>

      <div className={css.field}>
        <span className={css.fieldLabel}>Travellers</span>
        <div className={css.travellerToggle}>
          <label className={css.toggleOption}>
            <input
              checked={row.travellerMode === 'default'}
              name={`tr-mode-${row.id}`}
              onChange={() => onChange({ travellerMode: 'default' })}
              type="radio"
            />
            <span>
              Itinerary default
              <span className={css.toggleHint}> · {defaultSummary}</span>
            </span>
          </label>
          <label className={css.toggleOption}>
            <input
              checked={row.travellerMode === 'custom'}
              name={`tr-mode-${row.id}`}
              onChange={() =>
                onChange({
                  travellerMode: 'custom',
                  // Seed the counter list from the itinerary default the
                  // first time the consultant flips to "Custom" so the
                  // form starts populated. Already-customised chapters
                  // keep their existing counts on re-toggle.
                  travellerCounts:
                    row.travellerCounts.length > 0
                      ? row.travellerCounts
                      : defaultTravellers,
                })
              }
              type="radio"
            />
            <span>Custom for this chapter</span>
          </label>
        </div>
        {row.travellerMode === 'custom' && (
          <TravellerCountList
            disabled={isPending}
            emptyPlaceholder={
              travellerTypeOptions.length === 0
                ? 'No traveller types configured.'
                : null
            }
            onChange={(next) => onChange({ travellerCounts: next })}
            travellerTypes={travellerTypeOptions}
            value={row.travellerCounts}
          />
        )}
      </div>
    </div>
  );
};
